import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, MessageSquare, Star, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminReviewsPanel({ token, isDarkMode }) {
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(false);

  const fetchReviews = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/reviews`, {
        params: { status_filter: filter },
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews(response.data.reviews || []);
      setCounts(response.data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }, [filter, token]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const moderateReview = async (reviewId, status) => {
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Review ${status}`);
      fetchReviews();
    } catch (error) {
      toast.error("Failed to update review");
    }
  };

  const statusTone = {
    pending: "bg-yellow-500/15 text-yellow-300 border-yellow-400/20",
    approved: "bg-emerald-500/15 text-emerald-300 border-emerald-400/20",
    rejected: "bg-red-500/15 text-red-300 border-red-400/20",
  };

  return (
    <div className="space-y-5" data-testid="admin-reviews-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-black"><MessageSquare className="h-5 w-5 text-[#00f2ea]" /> Review Moderation</h2>
          <p className="text-sm text-white/45">Approve testimonials before they appear on the public landing page.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {["pending", "approved", "rejected", "all"].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              data-testid={`admin-reviews-filter-${status}`}
              className={`rounded-full border px-3 py-1.5 text-sm font-bold capitalize transition-[background-color,color] ${filter === status ? "border-[#00f2ea]/40 bg-[#00f2ea]/15 text-[#00f2ea]" : "border-white/10 bg-white/5 text-white/50 hover:text-white"}`}
              type="button"
            >
              {status} {status !== "all" ? counts[status] || 0 : ""}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-8 text-center text-white/50" data-testid="admin-reviews-loading">Loading reviews...</div>
      ) : reviews.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-8 text-center text-white/50" data-testid="admin-reviews-empty">No reviews in this queue.</div>
      ) : (
        <div className="grid gap-4" data-testid="admin-reviews-list">
          {reviews.map((review) => (
            <article key={review.id} className={`rounded-xl border p-5 ${isDarkMode ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-white"}`} data-testid={`admin-review-card-${review.id}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <Badge className={`border ${statusTone[review.status] || statusTone.pending}`} data-testid={`admin-review-status-${review.id}`}>{review.status}</Badge>
                    {review.verified_purchase && <Badge className="border border-cyan-300/20 bg-cyan-300/10 text-cyan-200">Verified</Badge>}
                  </div>
                  <h3 className="text-lg font-black">{review.title || "Untitled review"}</h3>
                  <p className="text-sm text-white/45">{review.display_name} • {new Date(review.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-1 text-yellow-300">
                  {Array.from({ length: review.rating || 0 }).map((_, index) => <Star key={index} className="h-4 w-4 fill-yellow-300" />)}
                </div>
              </div>
              <p className="mt-4 leading-relaxed text-white/70">{review.content}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button onClick={() => moderateReview(review.id, "approved")} data-testid={`admin-review-approve-${review.id}`} className="bg-emerald-500 text-black hover:bg-emerald-400"><CheckCircle2 className="mr-2 h-4 w-4" /> Approve</Button>
                <Button onClick={() => moderateReview(review.id, "rejected")} data-testid={`admin-review-reject-${review.id}`} variant="outline" className="border-red-400/40 text-red-300 hover:bg-red-500/10"><XCircle className="mr-2 h-4 w-4" /> Reject</Button>
                {review.status !== "pending" && <Button onClick={() => moderateReview(review.id, "pending")} data-testid={`admin-review-pending-${review.id}`} variant="outline">Move to pending</Button>}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}